import { Typography } from "@mui/material";
import { CreateLinearIssueDialog } from "components/CreateLinearIssueDialog";
import { useCallback, useMemo } from "react";

interface WasteSubmissionLinearIssueDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
  submissionTitle: string | null;
  submissionType: string;
  facilityName: string | null;
  state: string | null;
  tenantName: string;
  epaIds: string[];
}

export function WasteSubmissionLinearIssueDialog({
  open,
  onClose,
  submissionId,
  submissionTitle,
  submissionType,
  facilityName,
  state,
  tenantName,
  epaIds,
}: WasteSubmissionLinearIssueDialogProps) {
  const submissionUrl = useMemo(() => {
    return `${window.location.origin}/staff/fulfillment/waste/submissions/${submissionId}`;
  }, [submissionId]);

  const defaultTitle = useMemo(() => {
    const statePrefix = state ? `[${state}] ` : "";
    const facility = facilityName ?? "Unknown Facility";
    return `${statePrefix}Waste Submission: ${facility} - ${submissionType}`;
  }, [state, facilityName, submissionType]);

  const buildDescription = useCallback(() => {
    return [
      `**Submission**: ${submissionTitle ?? "Waste Submission"}`,
      `**Type**: ${submissionType}`,
      "",
      `**Waste Submission Page**: ${submissionUrl}`,
      "",
      `**Facility**: ${facilityName ?? "—"}`,
      `**State**: ${state ?? "—"}`,
      `**EPA IDs**: ${epaIds.length > 0 ? epaIds.join(", ") : "—"}`,
      "",
      `**Organization**: ${tenantName}`,
      "",
      `**Submission ID**: ${submissionId}`,
    ].join("\n");
  }, [
    submissionTitle,
    submissionType,
    submissionUrl,
    facilityName,
    state,
    epaIds,
    tenantName,
    submissionId,
  ]);

  const previewContent = useMemo(
    () => (
      <>
        <Typography variant="subtitle2" gutterBottom>
          Submission
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {submissionTitle ?? "Waste Submission"} ({submissionType})
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Facility
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {facilityName ?? "—"} ({state ?? "—"})
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          EPA IDs
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {epaIds.length > 0 ? epaIds.join(", ") : "—"}
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Organization
        </Typography>
        <Typography variant="body2">{tenantName}</Typography>
      </>
    ),
    [submissionTitle, submissionType, facilityName, state, epaIds, tenantName]
  );

  return (
    <CreateLinearIssueDialog
      open={open}
      onClose={onClose}
      defaultTitle={defaultTitle}
      buildDescription={buildDescription}
      previewContent={previewContent}
    />
  );
}
